import React, { useState, useRef, useCallback } from 'react';
import { Camera, Upload, X, Check, Loader2, AlertCircle, Utensils, Plus } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MealPhotoScanner = ({ selectedDate, onMealLogged, onClose }) => {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [logging, setLogging] = useState(false);
  const [selectedFoods, setSelectedFoods] = useState([]);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleImageSelect = useCallback((file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a JPEG, PNG, or WebP image');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }
    
    setError(null);
    setResults(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setImage(e.target.result);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleImageSelect(file);
  };

  const analyzePhoto = async () => {
    if (!image) return;
    
    setAnalyzing(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/nutrition/analyze-photo`,
        { 
          image_base64: image,
          meal_type: 'any'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResults(response.data);
      // Select all foods by default
      setSelectedFoods(response.data.foods.map((_, idx) => idx));
    } catch (err) {
      console.error('Analysis error:', err);
      if (err.response?.status === 403) {
        setError('This is a premium feature. Please upgrade to access meal photo scanning.');
      } else {
        setError(err.response?.data?.detail || 'Failed to analyze photo. Please try again.');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleFoodSelection = (index) => {
    setSelectedFoods(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const logSelectedFoods = async () => {
    if (!results || selectedFoods.length === 0) return;
    
    setLogging(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const foodsToLog = selectedFoods.map(idx => results.foods[idx]);
      
      await axios.post(
        `${API}/api/nutrition/analyze-photo/log`,
        { 
          foods: foodsToLog,
          date: selectedDate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (onMealLogged) onMealLogged();
      if (onClose) onClose();
    } catch (err) {
      console.error('Logging error:', err);
      setError(err.response?.data?.detail || 'Failed to log foods. Please try again.');
    } finally {
      setLogging(false);
    }
  };

  const resetScanner = () => {
    setImage(null);
    setImagePreview(null);
    setResults(null);
    setError(null);
    setSelectedFoods([]);
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-orange-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="bg-stone-900/95 backdrop-blur-sm rounded-xl border border-blood-900/30 p-4 sm:p-6" data-testid="meal-photo-scanner">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-blood-400" />
          <h3 className="text-lg font-bold text-white">Meal Photo Scanner</h3>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="p-1 hover:bg-stone-800 rounded-lg transition-colors"
            data-testid="close-scanner-btn"
          >
            <X className="w-5 h-5 text-stone-300" />
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg flex items-start gap-2" data-testid="scanner-error">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Image Upload Section */}
      {!imagePreview && (
        <div className="space-y-4">
          <div 
            className="border-2 border-dashed border-stone-700 rounded-xl p-8 text-center hover:border-blood-500/50 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-area"
          >
            <Upload className="w-12 h-12 text-stone-400 mx-auto mb-3" />
            <p className="text-stone-200 font-medium mb-1">Upload a meal photo</p>
            <p className="text-stone-400 text-sm">JPEG, PNG, or WebP (max 10MB)</p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 py-3 px-4 bg-stone-800 hover:bg-stone-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="choose-file-btn"
            >
              <Upload className="w-4 h-4" />
              Choose File
            </button>
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="flex-1 py-3 px-4 bg-blood-600 hover:bg-blood-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="take-photo-btn"
            >
              <Camera className="w-4 h-4" />
              Take Photo
            </button>
          </div>
          
          {/* Hidden inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            className="hidden"
            data-testid="file-input"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={handleFileChange}
            className="hidden"
            data-testid="camera-input"
          />
        </div>
      )}

      {/* Image Preview */}
      {imagePreview && !results && (
        <div className="space-y-4">
          <div className="relative rounded-xl overflow-hidden">
            <img 
              src={imagePreview} 
              alt="Meal preview" 
              className="w-full max-h-64 object-cover"
              data-testid="image-preview"
            />
            <button
              onClick={resetScanner}
              className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-lg transition-colors"
              data-testid="remove-image-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
          
          <button
            onClick={analyzePhoto}
            disabled={analyzing}
            className="w-full py-3 px-4 bg-gradient-to-r from-blood-600 to-blood-500 hover:from-blood-500 hover:to-blood-400 disabled:opacity-50 text-white rounded-lg font-bold transition-all flex items-center justify-center gap-2"
            data-testid="analyze-btn"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Utensils className="w-5 h-5" />
                Analyze Meal
              </>
            )}
          </button>
        </div>
      )}

      {/* Analysis Results */}
      {results && (
        <div className="space-y-4" data-testid="analysis-results">
          {/* Meal Description */}
          <div className="p-3 bg-stone-800/50 rounded-lg">
            <p className="text-stone-200 text-sm">{results.meal_description}</p>
          </div>
          
          {/* Total Summary */}
          <div className="grid grid-cols-4 gap-2 p-3 bg-blood-900/20 border border-blood-900/30 rounded-lg">
            <div className="text-center">
              <p className="text-xl font-bold text-blood-400">{Math.round(results.total_calories)}</p>
              <p className="text-xs text-stone-400">Calories</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-blue-400">{Math.round(results.total_protein)}g</p>
              <p className="text-xs text-stone-400">Protein</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-amber-400">{Math.round(results.total_carbs)}g</p>
              <p className="text-xs text-stone-400">Carbs</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-purple-400">{Math.round(results.total_fats)}g</p>
              <p className="text-xs text-stone-400">Fats</p>
            </div>
          </div>
          
          {/* Food Items */}
          <div className="space-y-2">
            <p className="text-stone-300 text-sm font-medium">Detected Foods (tap to select/deselect)</p>
            {results.foods.map((food, idx) => (
              <div
                key={idx}
                onClick={() => toggleFoodSelection(idx)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedFoods.includes(idx)
                    ? 'bg-blood-900/30 border-blood-500/50'
                    : 'bg-stone-800/50 border-stone-700/50 opacity-60'
                }`}
                data-testid={`food-item-${idx}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{food.name}</p>
                      <span className={`text-xs ${getConfidenceColor(food.confidence)}`}>
                        ({food.confidence})
                      </span>
                    </div>
                    <p className="text-stone-400 text-sm">{food.portion_size}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-blood-400 font-bold">{Math.round(food.calories)} cal</p>
                    <p className="text-stone-400 text-xs">
                      P:{Math.round(food.protein)}g C:{Math.round(food.carbs)}g F:{Math.round(food.fats)}g
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedFoods.includes(idx)
                      ? 'border-blood-500 bg-blood-500'
                      : 'border-stone-600'
                  }`}>
                    {selectedFoods.includes(idx) && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Suggestions */}
          {results.suggestions && (
            <div className="p-3 bg-amber-900/20 border border-amber-700/30 rounded-lg">
              <p className="text-amber-200 text-sm">💡 {results.suggestions}</p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={resetScanner}
              className="flex-1 py-3 px-4 bg-stone-800 hover:bg-stone-700 text-white rounded-lg font-medium transition-colors"
              data-testid="scan-another-btn"
            >
              Scan Another
            </button>
            <button
              onClick={logSelectedFoods}
              disabled={logging || selectedFoods.length === 0}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-blood-600 to-blood-500 hover:from-blood-500 hover:to-blood-400 disabled:opacity-50 text-white rounded-lg font-bold transition-all flex items-center justify-center gap-2"
              data-testid="log-foods-btn"
            >
              {logging ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Logging...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Log {selectedFoods.length} Item{selectedFoods.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPhotoScanner;
